import { Badge, Checkbox, Switch, Text } from '@pentabd/ui';
import { ReactEChartsComponent, ResponsiveGridLayoutComponent } from './index';
import { DivisionChartComponentBodyPropsType } from '@type/dashboard';
import {
  divisionBarChartOption,
  divisionLineChartOption,
  divisionConstituencyCentersOption,
} from '../options';
import { memo, useCallback, useEffect } from 'react';
import { numberLanguageFormatter } from '@utils/utils';
import { getAccess } from '@utils/accessControl';

export const DivisionChartComponentBody = memo(
  ({
    index,
    title,
    handler,
    divisionId,
    handleQuery,
    divisionData,
    initialAsonId,
    toggleDivision,
    switchChartType,
    dashboardCenter,
    isCenterLoading,
    isDivisionLoading,
    searchParams,
  }: DivisionChartComponentBodyPropsType) => {
    const election_schedule_id =
      searchParams?.get('election_schedule_id') || '';

    const divisionConstituencyCentersOptionCb = useCallback(
      divisionConstituencyCentersOption,
      [dashboardCenter],
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleQueryCb = useCallback(handleQuery, [
      divisionId,
      initialAsonId,
      election_schedule_id,
    ]);

    useEffect(() => {
      handleQueryCb(divisionId, initialAsonId, election_schedule_id);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [divisionId, initialAsonId, election_schedule_id]);

    return (
      <div
        id={`${divisionId}`}
        className="w-100 m-0 my-10 px-5 py-10 bg-light rounded border position-relative"
      >
        {/* title  */}
        <div
          className={`px-5 d-flex justify-content-between align-items-center ${
            toggleDivision ? 'mb-8' : ''
          }`}
        >
          <Text
            weight="bold"
            component="h2"
            className="summary-title"
            size="md"
          >
            {title}
          </Text>
          <div>
            <Checkbox
              id={`toggleDivision-${index}`}
              name={`toggleDivision-${index}`}
              onChange={(e) => {
                e.target.checked
                  ? handler(index, 'toggleDivision', true)
                  : handler(index, 'toggleDivision', false);
              }}
              checked={toggleDivision}
              shape="square"
              size="lg"
              controlling
              type="info"
            />
          </div>
        </div>

        {/* chart  */}
        {toggleDivision ? (
          <div className="row m-0 p-0">
            {/* bar or line chart  */}
            <div className="col-xl-8 col-12 p-0 m-0">
              <ResponsiveGridLayoutComponent
                isResizable={true}
                isDraggable={false}
                margin={[10, 0]}
              >
                <div
                  key={`${index}-bar-or-line`}
                  className="w-100 h-100 rounded border bg-white"
                  data-grid={{
                    x: 0,
                    y: 0,
                    w: 12,
                    h: 4,
                  }}
                >
                  <div className="draggableCancelSwitch switch-custom-position">
                    <Switch
                      id={`switchChartType-${index}`}
                      name={`switchChartType-${index}`}
                      checked={switchChartType}
                      onChange={(e) => {
                        e.target.checked
                          ? handler(index, 'switchChartType', true)
                          : handler(index, 'switchChartType', false);
                      }}
                      size="sm"
                      controlling
                    />
                  </div>

                  <div className="w-100 h-100 draggableCancelSwitch px-10 scrollbar">
                    <div className="w-100 h-100 overflow-x-auto overflow-y-hidden">
                      <ReactEChartsComponent
                        option={
                          switchChartType
                            ? divisionBarChartOption(divisionData)
                            : divisionLineChartOption(divisionData)
                        }
                        divisionData={divisionData}
                        handleQuery={handleQueryCb}
                        isLoading={isDivisionLoading}
                        colorIndex={divisionId}
                        minWidth={`${
                          (divisionData?.election_setting_value?.length || 0) *
                          38
                        }px`}
                      />
                    </div>
                  </div>
                </div>
              </ResponsiveGridLayoutComponent>
            </div>

            {/* pie chart  */}
            <div className="col-xl-4 col-12 p-0 m-0 mt-10 mt-xl-0">
              <ResponsiveGridLayoutComponent
                isResizable={true}
                isDraggable={false}
                margin={[10, 0]}
              >
                <div
                  key={`${index}-pie`}
                  className="h-100 rounded bg-white border p-10"
                  data-grid={{ x: 0, y: 0, w: 12, h: 4 }}
                >
                  <div
                    className={`pie-chat-title-position draggableCancelSwitch ${
                      getAccess(dashboardCenter?.election_settings_id)
                        ? 'pointer'
                        : ''
                    }`}
                    onClick={(e) => {
                      if (
                        dashboardCenter?.election_settings_id &&
                        getAccess(dashboardCenter?.election_settings_id)
                      ) {
                        window.open(
                          `${window.location.origin}/center-wise-results/${
                            dashboardCenter?.election_settings_id || ''
                          }?election_schedule_id=${election_schedule_id}`,
                        );
                      }
                    }}
                  >
                    <Badge
                      label={`${numberLanguageFormatter(
                        dashboardCenter?.election_settings_id || '',
                      )} - ${dashboardCenter?.election_settings}`}
                      type="info"
                      size="sm"
                      className="text-nowrap w-max"
                    />
                  </div>
                  <div className="h-100 pt-10">
                    {dashboardCenter?.centers_info?.length === 0 ? (
                      <div className="d-flex flex-column justify-content-center align-items-center">
                        <img
                          src={'/pie.svg'}
                          alt="No data"
                          height={500}
                          width={500}
                        />
                      </div>
                    ) : (
                      <ReactEChartsComponent
                        option={divisionConstituencyCentersOptionCb(
                          dashboardCenter,
                        )}
                        handleQuery={handleQueryCb}
                        isLoading={isCenterLoading}
                        colorIndex={divisionId}
                      />
                    )}
                  </div>
                </div>
              </ResponsiveGridLayoutComponent>
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
    );
  },
);
